import {
	type QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type {
	InboxConversationFiltersDto,
	InboxConversationWithMessagesDto,
	SendInboxMessageDto,
} from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

function invalidateInboxQueries(queryClient: QueryClient, companyId: number) {
	return Promise.all([
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Inbox.Conversations(companyId),
		}),
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Inbox.ConversationPrefix(companyId),
		}),
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Inbox.UnreadCount(companyId),
		}),
	]);
}

export function useInboxConversations(filters?: InboxConversationFiltersDto) {
	const company = useCompany();

	return useSuspenseQuery({
		queryKey: [...QueryKeys.Inbox.Conversations(company.id), filters],
		queryFn: async () => (await Api.GetInboxConversations(filters)).data,
	});
}

export function useInboxConversation(conversationId: number) {
	const company = useCompany();

	return useSuspenseQuery({
		queryKey: QueryKeys.Inbox.Conversation(company.id, conversationId),
		queryFn: async () => (await Api.GetInboxConversation(conversationId)).data,
	});
}

export function useInboxUnreadCount() {
	const company = useCompany();

	return useQuery({
		queryKey: QueryKeys.Inbox.UnreadCount(company.id),
		queryFn: async () => (await Api.GetInboxUnreadCount()).data,
		refetchInterval: 60000, // Refetch every minute
	});
}

export function useInboxSyncStatus() {
	const company = useCompany();
	const queryClient = useQueryClient();
	const wasSyncing = useRef(false);
	const lastSuccessfulSyncAt = useRef<string | undefined>(undefined);

	const query = useQuery({
		queryKey: QueryKeys.Inbox.SyncStatus(company.id),
		queryFn: async () => {
			const data = (await Api.GetInboxSyncStatus()).data;
			const errors = data.filter((s) => s.status === "error");
			if (errors.length > 0) {
				// biome-ignore lint/suspicious/noConsole: staging debug — server logs are not visible there
				console.error("[inbox] Sync failed — platform errors:", errors);
				for (const entry of errors) {
					// biome-ignore lint/suspicious/noConsole: staging debug — server logs are not visible there
					console.error(
						`[inbox] ${entry.platform} (${entry.syncType}): ${entry.errorMessage ?? "Unknown error"}`,
						entry,
					);
				}
			}
			return data;
		},
		refetchInterval: 3000,
	});

	useEffect(() => {
		if (!query.data) {
			return;
		}

		const isSyncing = query.data.some((s) => s.status === "syncing");
		const lastSuccessfulSync = query.data
			.map((s) => s.lastSyncAt)
			.filter((d): d is string => !!d)
			.sort()
			.pop();

		const syncJustFinished = wasSyncing.current && !isSyncing;
		const lastSyncChanged =
			!!lastSuccessfulSync &&
			!!lastSuccessfulSyncAt.current &&
			lastSuccessfulSync !== lastSuccessfulSyncAt.current;

		if (isSyncing || syncJustFinished || lastSyncChanged) {
			void invalidateInboxQueries(queryClient, company.id);
		}

		wasSyncing.current = isSyncing;
		if (lastSuccessfulSync) {
			lastSuccessfulSyncAt.current = lastSuccessfulSync;
		}

		if (!isSyncing) {
			return;
		}

		const interval = setInterval(() => {
			void invalidateInboxQueries(queryClient, company.id);
		}, 3000);

		return () => {
			clearInterval(interval);
		};
	}, [company.id, query.data, queryClient]);

	return query;
}

export function useInboxActions() {
	const company = useCompany();
	const queryClient = useQueryClient();

	const markAsRead = useMutation({
		mutationFn: async (conversationId: number) => {
			await Api.MarkInboxConversationRead(conversationId);
		},
		onSuccess: async (_data, conversationId) => {
			// Clear unread locally first so the view effect cannot re-fire.
			queryClient.setQueryData(
				QueryKeys.Inbox.Conversation(company.id, conversationId),
				(old: InboxConversationWithMessagesDto | undefined) =>
					old
						? {
								...old,
								conversation: { ...old.conversation, unreadCount: 0 },
							}
						: old,
			);
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.Conversation(company.id, conversationId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.Conversations(company.id),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.UnreadCount(company.id),
			});
		},
	});

	const archiveConversation = useMutation({
		mutationFn: async (conversationId: number) => {
			await Api.ArchiveInboxConversation(conversationId);
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.Conversations(company.id),
			});
		},
	});

	const unarchiveConversation = useMutation({
		mutationFn: async (conversationId: number) => {
			await Api.UnarchiveInboxConversation(conversationId);
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.Conversations(company.id),
			});
		},
	});

	const sendMessage = useMutation({
		mutationFn: async (dto: SendInboxMessageDto) => {
			await Api.SendInboxMessage(dto);
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.Conversation(company.id, dto.conversationId),
			});
		},
	});

	const suggestReply = useMutation({
		mutationFn: async (vars: { conversationId: number; messageId: number }) => {
			const result = await Api.SuggestInboxReply(
				vars.conversationId,
				vars.messageId,
			);
			return result.data.reply;
		},
	});

	const setCommentHidden = useMutation({
		mutationFn: async (vars: {
			conversationId: number;
			messageId: number;
			hidden: boolean;
		}) => {
			await Api.SetInboxCommentHidden(vars.conversationId, vars.messageId, {
				hidden: vars.hidden,
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.Conversation(company.id, vars.conversationId),
			});
		},
	});

	const triggerSync = useMutation({
		mutationFn: async () => {
			// biome-ignore lint/suspicious/noConsole: staging debug — server logs are not visible there
			console.info("[inbox] Sync Now triggered");
			await Api.TriggerInboxSync();
			// Don't wait for sync to complete - it runs in background
			// Refetch sync status instead
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.SyncStatus(company.id),
			});
		},
		onSuccess: () => {
			// biome-ignore lint/suspicious/noConsole: staging debug — server logs are not visible there
			console.info(
				"[inbox] Sync started in background — status will poll; errors will log here when ready",
			);
		},
		onError: (error) => {
			// biome-ignore lint/suspicious/noConsole: staging debug — server logs are not visible there
			console.error("[inbox] Failed to trigger sync:", error);
		},
	});

	return {
		markAsRead,
		archiveConversation,
		unarchiveConversation,
		sendMessage,
		suggestReply,
		setCommentHidden,
		triggerSync,
	};
}
